import React, { useState } from 'react';
import cx from 'classnames';
import type { LoginResult } from '@zola-helpers/client/dist/es/redux/auth/authActions';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import AuthForm from './components/AuthForm/AuthForm';
import AuthFormV2 from './components/AuthForm/AuthFormV2';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import TwoFactorForm from './components/TwoFactorForm';
import MinimalAuthForm from './components/MinimalAuthForm';

import {
  AuthState,
  CommonAuthFormProps,
  MinimalAuthFormType,
  MinimalAuthFormValues,
} from './types';
import { recaptchaKey } from './utils/helpers';

import styles from './AuthForms.module.less';

export type AuthFormsProps = {
  showSignupOnInit?: boolean;
  loginCallback: () => void;
  loginLinkCallback?: () => void;
  signupCallback?: () => void;
  signupLinkCallback: () => boolean;
  className?: string;
  initialAuthState?: AuthState;
  useRefactoredRecaptcha?: boolean;
} & (
  | {
      showV2?: boolean;
      showMinimalAuthForm?: false;
    }
  | {
      showV2?: false;
      showMinimalAuthForm: true;
      initialValues?: Partial<MinimalAuthFormValues>;
      showMinimalAuthFormNameInput?: boolean;
      initialMinimalAuthFormState?: MinimalAuthFormType;
      authHed?: string;
      loginCTA?: string;
      signupCTA?: string;
    }
);

const AuthForms: React.FC<AuthFormsProps> = (props) => {
  const {
    loginCallback,
    loginLinkCallback = () => null,
    signupCallback = () => null,
    signupLinkCallback,
    showSignupOnInit = false,
    showV2 = false,
    className,
    initialAuthState,
    useRefactoredRecaptcha = false,
  } = props;

  // TODO: cleanup "showSignupOnInit" prop in favor of initialAuthState
  const initialFormState =
    initialAuthState || (showSignupOnInit ? AuthState.SIGNUP : AuthState.LOGIN);
  const [authState, setAuthState] = useState<AuthState>(initialFormState);
  const [sessionId, setSessionId] = useState('');
  const [notifiedValue, setNotifiedValue] = useState('');
  const [partiallyLoggedInUserId, setPartiallyLoggedInUserId] = useState('');
  const [passwordInputType, setPasswordInputType] = useState('password');

  const onTogglePasswordVisibility = (): void => {
    setPasswordInputType((prevState) => (prevState === 'password' ? 'text' : 'password'));
  };

  const onClickSignup = (): void => {
    // in most instances, we redirect to an onboarding flow;
    // signupLinkCallback will handle those redirects, but some products allow
    // direct sign up, in which case, signupLinkCallback will return true
    if (signupLinkCallback()) setAuthState(AuthState.SIGNUP);
  };

  const onClickLogin = (): void => {
    // if a user clicks the "Already have an account Sign in here" link
    loginLinkCallback();
    setAuthState(AuthState.LOGIN);
  };

  // includes successful, or request for 2FA
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onAny200Login = (data: LoginResult, email = ''): void => {
    if (data.verification_required_at) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setSessionId(data.session_id);
      setNotifiedValue(data.notified_value || '');
      setPartiallyLoggedInUserId(data.userId as string); // userId will exist if verification_required_at exists
      return setAuthState(AuthState.TWOFA);
    }
    return loginCallback();
  };

  const authFormProps: CommonAuthFormProps = {
    authState,
    setAuthState,
    onAny200Login,
    signupCallback,
    onClickSignup,
    passwordInputType,
    onTogglePasswordVisibility,
    useRefactoredRecaptcha,
  };

  const whichGenericAuthForm = () => {
    if (props.showMinimalAuthForm) {
      const {
        showMinimalAuthFormNameInput = false,
        authHed,
        loginCTA,
        signupCTA,
        initialMinimalAuthFormState,
        initialValues,
      } = props;

      return (
        <MinimalAuthForm
          {...authFormProps}
          onClickLogin={onClickLogin}
          authHed={authHed}
          loginCTA={loginCTA}
          signupCTA={signupCTA}
          includeNameInput={showMinimalAuthFormNameInput}
          initialMinimalAuthFormState={initialMinimalAuthFormState}
          initialValues={initialValues}
        />
      );
    }

    if (showV2) {
      return <AuthFormV2 {...authFormProps} />;
    }
    return <AuthForm {...authFormProps} />;
  };

  const renderForm = () => {
    switch (authState) {
      case AuthState.FORGOT:
        return <ForgotPasswordForm setAuthState={setAuthState} onClickSignup={onClickSignup} />;
      case AuthState.TWOFA:
        return (
          <TwoFactorForm
            notifiedValue={notifiedValue}
            sessionId={sessionId}
            userObjectId={partiallyLoggedInUserId}
            verifiedCallback={loginCallback}
            isLogin
          />
        );
      case AuthState.SIGNUP:
      case AuthState.LOGIN:
      default:
        if (useRefactoredRecaptcha) {
          return whichGenericAuthForm();
        }
        return (
          <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
            {whichGenericAuthForm()}
          </GoogleReCaptchaProvider>
        );
    }
  };

  return (
    <div
      id="authForms"
      className={cx(
        showV2 || props.showMinimalAuthForm ? styles.containerWide : styles.container,
        className
      )}
    >
      {renderForm()}
    </div>
  );
};

export default AuthForms;
